import { Link } from 'react-router-dom';
import PageShell from '../components/PageShell';
import PolicyLayout from './PolicyLayout';
import {
  ADDRESS,
  BRAND_NAME,
  COMPANY_NAME,
  POLICY_UPDATED,
  SITE_DOMAIN,
  SUPPORT_PHONE_LABEL,
  SUPPORT_PHONE_TEL,
} from '../constants/site';
import { openZohoChat } from '../utils/openZohoChat';

export default function RefundPolicy() {
  return (
    <PageShell>
      <PolicyLayout
        title="Refund Policy"
        updated={POLICY_UPDATED}
        intro={`${COMPANY_NAME} stands behind the support sold under the ${BRAND_NAME} name. Below you'll find the situations in which we return your money for a paid service ordered on ${SITE_DOMAIN}, over the phone, or in live chat, along with the steps for filing a claim.`}
      >
        <h2>Scope</h2>
        <p>
          The rules on this page apply solely to support work that {COMPANY_NAME} bills for
          directly, namely single support sessions and multi-visit support plans. They don&apos;t
          extend to Family Tree Maker licenses, Ancestry memberships, TreeVault services, or anything
          else you bought from a publisher or a different retailer. For those, check the refund terms
          of the company that took your payment.
        </p>

        <h2>Situations That Qualify</h2>
        <ul>
          <li>
            <strong>We couldn&apos;t fix it:</strong> when our team was unable to solve the problem
            you paid us to address, a full refund is available if you ask within 7 days of your
            purchase date.
          </li>
          <li>
            <strong>You canceled before we began:</strong> calling off a session ahead of its start
            gets every dollar back.
          </li>
          <li>
            <strong>Billing mistakes:</strong> a double charge or an amount that doesn&apos;t match
            what you agreed to is returned in full once we verify the error.
          </li>
          <li>
            <strong>Support plans:</strong> a plan you haven&apos;t used yet can be canceled for a
            complete refund during the first 7 days. Past that point, we may return part of the cost,
            weighing how much of the plan term remains against the help you&apos;ve already received.
          </li>
        </ul>

        <h2>Situations That Don&apos;t Qualify</h2>
        <ul>
          <li>Work that was carried out and did solve the problem.</li>
          <li>Claims submitted once 7 days have passed since the purchase.</li>
          <li>
            Trouble that stems from a hardware fault, a third-party service going down, or changes
            made to the computer after our session was over.
          </li>
          <li>Cases where the technician was not permitted to perform the steps a fix required.</li>
        </ul>

        <h2>Filing a Refund Request</h2>
        <ol>
          <li>
            Reach us by phone at <a href={SUPPORT_PHONE_TEL}>{SUPPORT_PHONE_LABEL}</a>, through{' '}
            <button type="button" className="text-link" onClick={openZohoChat}>
              live chat
            </button>
            , or by using the{' '}
            <Link to="/contact-us">contact form</Link>.
          </li>
          <li>Let us know you are asking for a refund and share the details listed below.</li>
          <li>We check the request against our records and the notes from your session.</li>
          <li>You hear back from us with a decision, and approved refunds are issued right away.</li>
        </ol>
        <h3>Details we&apos;ll need</h3>
        <ul>
          <li>The name the purchase was made under.</li>
          <li>The email address or phone number you gave when paying.</li>
          <li>The date your session took place or your plan was bought.</li>
          <li>A sentence or two on why the service didn&apos;t meet your needs.</li>
        </ul>

        <h2>Review and Payout Timeline</h2>
        <p>
          Expect a decision on your request within 2 business days. Once approved, the amount is
          credited to the card or account you originally paid with. Banks and card issuers differ in how quickly
          they post credits, so allow 5–10 business days before the refund shows up on your statement.
        </p>

        <h2>Before You Dispute a Charge</h2>
        <p>
          If something about a payment looks wrong, get in touch with us before filing a dispute with
          your bank. In most cases we can settle the matter far more quickly directly.
        </p>

        <h2>Policy Revisions</h2>
        <p>
          We may revise these refund rules. Any revision covers only orders placed on or after the
          date shown near the top of the page.
        </p>

        <h2>Contact Details</h2>
        <p>
          <strong>{COMPANY_NAME}</strong>
          <br />
          {ADDRESS}
          <br />
          Billing questions: <a href={SUPPORT_PHONE_TEL}>{SUPPORT_PHONE_LABEL}</a>
          <br />
          Site: {SITE_DOMAIN}
        </p>
      </PolicyLayout>
    </PageShell>
  );
}
